import React, { useState } from 'react';
import { X, Settings, Trash2, AlertTriangle, ExternalLink, Users, Shield } from 'lucide-react';
import { Button as UIButton } from './ui/button';
import { Badge } from './ui/badge';
import SharePointManagementModal from './SharePointManagementModal';
import ProductPermissionsModal from './ProductPermissionsModal';

const ProductSettingsModal = ({ product, isOpen, onClose, onUpdate, onDelete }) => {
  const [activeTab, setActiveTab] = useState('sharepoint');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSharePointModal, setShowSharePointModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);

  const tabs = [
    { id: 'sharepoint', label: 'SharePoint', icon: ExternalLink },
    { id: 'permissions', label: 'Permissions', icon: Shield },
    { id: 'delete', label: 'Delete Product', icon: Trash2 },
  ];

  const handleDelete = async () => {
    try {
      await onDelete();
      onClose();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  if (!isOpen || !product) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Settings className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  Product Settings
                </h2>
                <p className="text-sm text-slate-600">
                  {product.title}
                </p>
              </div>
            </div>
            <UIButton
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-500 hover:text-slate-700"
            >
              <X className="w-5 h-5" />
            </UIButton>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            {activeTab === 'sharepoint' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">SharePoint Links</h3>
                    <p className="text-sm text-slate-600">
                      Manage SharePoint sites and documentation for this product
                    </p>
                  </div>
                  <UIButton
                    onClick={() => {
                      setShowSharePointModal(true);
                    }}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Manage SharePoint
                  </UIButton>
                </div>
                
                {/* SharePoint Links Preview */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                    <ExternalLink className="w-4 h-4" />
                    <span>Quick Links</span>
                  </div>
                  {product.sharepoint_links && product.sharepoint_links.length > 0 ? (
                    <div className="space-y-2">
                      {product.sharepoint_links.map((link) => (
                        <div key={link.id} className="flex items-center justify-between p-2 bg-white rounded border">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{link.display_name}</span>
                            {link.is_default && (
                              <Badge variant="secondary" className="text-xs">Default</Badge>
                            )}
                          </div>
                          <UIButton
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(link.sharepoint_url, '_blank')}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            Open
                          </UIButton>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No SharePoint links configured
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'permissions' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">User Permissions</h3>
                    <p className="text-sm text-slate-600">
                      Control which users can access and manage this product
                    </p>
                  </div>
                  <UIButton
                    onClick={() => {
                      setShowPermissionsModal(true);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Manage Permissions
                  </UIButton>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                    <Shield className="w-4 h-4" />
                    <span>Permission Management</span>
                  </div>
                  <p className="text-sm text-slate-500 text-center py-8">
                    Click "Manage Permissions" to configure user access for this product
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'delete' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-red-900">Delete Product</h3>
                    <p className="text-sm text-red-700">
                      This action cannot be undone. All product data, images, notes, and associated information will be permanently deleted.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-slate-900">What will be deleted:</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-slate-400 rounded-full"></div>
                      Product information and metadata
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-slate-400 rounded-full"></div>
                      All uploaded images and files
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-slate-400 rounded-full"></div>
                      Timeline notes and comments
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-slate-400 rounded-full"></div>
                      SharePoint link configurations
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-slate-400 rounded-full"></div>
                      User permissions and access settings
                    </li>
                  </ul>
                </div>

                {!showDeleteConfirm ? (
                  <div className="flex justify-end">
                    <UIButton
                      onClick={() => setShowDeleteConfirm(true)}
                      className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Product
                    </UIButton>
                  </div>
                ) : (
                  <div className="bg-red-100 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-red-900">Confirm Deletion</h4>
                      <UIButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowDeleteConfirm(false)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </UIButton>
                    </div>
                    <p className="text-sm text-red-700 mb-4">
                      Are you absolutely sure you want to delete "{product.title}"? This action cannot be undone.
                    </p>
                    <div className="flex justify-end gap-2">
                      <UIButton
                        variant="outline"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Cancel
                      </UIButton>
                      <UIButton
                        onClick={handleDelete}
                        className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Yes, Delete Product
                      </UIButton>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <SharePointManagementModal
        product={product}
        isOpen={showSharePointModal}
        onClose={() => setShowSharePointModal(false)}
        onUpdate={onUpdate}
      />
      <ProductPermissionsModal
        product={product}
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        onUpdate={onUpdate}
      />
    </>
  );
};

export default ProductSettingsModal;
